/**
 * 숫자만 받아 구분자를 붙여 보여 주는 입력칸의 공통 규칙.
 *
 * 반송장번호(`tracking.ts`)와 연락처(`phone-jp.ts`)가 같이 쓴다. 까다로운 건 커서다 —
 * 하이픈이 붙거나 떨어지면 글자 수가 달라지므로, 커서를 "앞에 있는 숫자 개수"로 따라가게 한다.
 */

export type DigitEdit = {
  /** 편집 직후 입력칸에 들어 있는 글(구분자가 섞여 있다). */
  value: string
  /** 그때의 커서 위치. */
  caret: number
  /** 지우는 편집이었는지 — 구분자만 지웠으면 옆 숫자를 마저 지운다. */
  deleting?: "backward" | "forward" | null
  /** 편집 전에 담고 있던 숫자. */
  previousDigits: string
}

export type DigitEditResult = {
  /** 숫자만 남긴 값. */
  digits: string
  /** 입력칸에 보일 값. */
  formatted: string
  /** 입력칸에 돌려놓을 커서 위치. */
  caret: number
}

export type DigitRules = {
  /** 입력칸의 글에서 숫자를 뽑는다(자릿수 제한도 여기서 건다). */
  digitsOf: (raw: string) => string
  /** 숫자를 보여 줄 모양으로. 입력 중(자릿수가 덜 찼을 때)에도 불린다. */
  format: (digits: string) => string
}

function countDigits(text: string): number {
  return (text.match(/\d/g) ?? []).length
}

/** 숫자 `count` 개를 지난 자리. 구분자 뒤가 아니라 숫자 바로 뒤에 선다. */
function caretAfterDigits(formatted: string, count: number): number {
  if (count <= 0) return 0
  let seen = 0
  for (let index = 0; index < formatted.length; index += 1) {
    if (/\d/.test(formatted[index])) {
      seen += 1
      if (seen === count) return index + 1
    }
  }
  return formatted.length
}

/**
 * 입력칸 한 번의 편집을 "숫자"와 "보이는 모양"으로 옮긴다.
 *
 * 백스페이스로 구분자만 지우면 화면이 그대로여서 아무 일도 안 한 것처럼 보이므로,
 * 그때는 그 앞 숫자를 마저 지운다(Delete 는 뒤 숫자).
 */
export function editDigits(edit: DigitEdit, rules: DigitRules): DigitEditResult {
  const caret = Math.max(0, Math.min(edit.caret, edit.value.length))
  let before = countDigits(edit.value.slice(0, caret))
  let digits = rules.digitsOf(edit.value)

  if (edit.deleting && digits === edit.previousDigits) {
    if (edit.deleting === "backward" && before > 0) {
      digits = digits.slice(0, before - 1) + digits.slice(before)
      before -= 1
    } else if (edit.deleting === "forward") {
      digits = digits.slice(0, before) + digits.slice(before + 1)
    }
  }

  const formatted = rules.format(digits)
  return { digits, formatted, caret: caretAfterDigits(formatted, before) }
}
